import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Loader2, AlertTriangle, CheckSquare, FileText, Brain, PenTool, CheckCircle, Eye, X, Lightbulb, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { useOffline } from '@/contexts/OfflineContext';
import { queueSafetyAudit } from '@/services/syncService';
import { waitForAuditAnalysis } from '@/utils/auditAnalysis';
import { saveToStore } from '@/services/offlineDB';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

// Simple UUID generator (no external dependency)
const generateId = () => {
  return 'offline-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
};

// Default fallback checklist (Construction)
const DEFAULT_CHECKLIST = {
  ppeCompliance: {
    title: 'PPE Compliance',
    items: [
      { key: 'hardHats', label: 'PPE properly worn (hard hats, safety glasses, gloves, boots)' },
      { key: 'highVis', label: 'High-visibility clothing worn where required' },
      { key: 'respiratory', label: 'Respiratory protection available/used if needed' },
      { key: 'hearing', label: 'Hearing protection available/used if needed' }
    ]
  },
  housekeeping: {
    title: 'Housekeeping & Work Area',
    items: [
      { key: 'workAreaClean', label: 'Work area clean and organized' },
      { key: 'tripHazards', label: 'Trip hazards removed (cords, debris, uneven surfaces)' },
      { key: 'materialsStacked', label: 'Materials stacked and secured' },
      { key: 'wasteDisposed', label: 'Waste disposed properly' }
    ]
  },
  emergencyPrep: {
    title: 'Emergency Preparedness',
    items: [
      { key: 'exitsClear', label: 'Emergency exits clear and marked' },
      { key: 'fireExtinguishers', label: 'Fire extinguishers accessible and inspected' },
      { key: 'firstAidKits', label: 'First aid kits stocked' },
      { key: 'emergencyProc', label: 'Emergency procedures posted' }
    ]
  },
  hazmat: {
    title: 'Hazardous Materials',
    items: [
      { key: 'chemLabeled', label: 'Chemicals properly labeled (GHS)' },
      { key: 'sdsAvailable', label: 'SDS sheets available' },
      { key: 'flammableStorage', label: 'Proper storage of flammables' },
      { key: 'spillKits', label: 'Spill kits available' }
    ]
  },
  toolsEquipment: {
    title: 'Tools & Equipment',
    items: [
      { key: 'equipmentGuards', label: 'Equipment guards in place' },
      { key: 'toolsCondition', label: 'Tools in good working condition' },
      { key: 'laddersInspected', label: 'Ladders inspected and used properly' },
      { key: 'handTools', label: 'Hand tools free of defects' },
      { key: 'powerTools', label: 'Power tools grounded and undamaged' }
    ]
  },
  electricalSafety: {
    title: 'Electrical Safety',
    items: [
      { key: 'cordDefects', label: 'Extension cords free of defects' },
      { key: 'gfciInPlace', label: 'GFCIs in place' },
      { key: 'panelsProtected', label: 'Open panels protected' },
      { key: 'lockoutTagout', label: 'Lockout/tagout procedures followed' }
    ]
  },
  fallProtection: {
    title: 'Fall Protection',
    items: [
      { key: 'guardrails', label: 'Guardrails, midrails, and toe boards installed' },
      { key: 'harnesses', label: 'Harnesses worn and anchored properly' },
      { key: 'scaffolds', label: 'Scaffolds inspected and tagged' },
      { key: 'ladderAngles', label: 'Ladder angles and stability correct' }
    ]
  },
  heavyEquipment: {
    title: 'Heavy Equipment & Vehicles',
    items: [
      { key: 'operatorsTrained', label: 'Operators trained and authorized' },
      { key: 'spotters', label: 'Spotters used when needed' },
      { key: 'inspectionLogs', label: 'Inspection logs completed' },
      { key: 'alarmsWorking', label: 'Alarms/back-up signals working' }
    ]
  },
  environmental: {
    title: 'Environmental Conditions',
    items: [
      { key: 'adequateLighting', label: 'Adequate lighting in work areas' },
      { key: 'weatherHazards', label: 'Weather hazards addressed (ice, heat, rain, wind)' },
      { key: 'noiseControlled', label: 'Noise levels controlled' }
    ]
  },
  materialHandling: {
    title: 'Material Handling',
    items: [
      { key: 'properLifting', label: 'Proper lifting techniques observed' },
      { key: 'mechanicalAids', label: 'Mechanical lifting aids used when required' }
    ]
  },
  jsaDocumentation: {
    title: 'JSA & Documentation',
    items: [
      { key: 'jsaCompleted', label: 'JSA completed for task' },
      { key: 'workersSigned', label: 'Workers signed JSA' },
      { key: 'ppeMatched', label: 'Correct PPE matched to JSA' },
      { key: 'newHazards', label: 'New hazards updated in JSA' }
    ]
  }
};

const SafetyAuditDialog = ({ open, onOpenChange, user, onSuccess, divisionIdOverride = null }) => {
  const { t } = useTranslation();
  const flags = useFeatureFlags();
  // Use divisionIdOverride if provided, otherwise fall back to user's divisionId
  const effectiveDivisionId = divisionIdOverride || user?.divisionId || null;
  
  // Offline support
  const { isOnline, updatePendingCount } = useOffline();
  
  const [currentTab, setCurrentTab] = useState('basic');
  const [submitting, setSubmitting] = useState(false);
  const [analyzingAudit, setAnalyzingAudit] = useState(false);
  const [jsaRecommendations, setJsaRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  
  // Draft audit ID for immediate photo uploads (created when first photo is selected)
  const [draftAuditId, setDraftAuditId] = useState(null);
  
  // Photo upload state: tracks each photo's upload/analysis status
  // Format: [{ file, preview, status: 'pending'|'uploading'|'analyzing'|'success'|'failed', analysis: {...} }]
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('construction');
  const [activeChecklist, setActiveChecklist] = useState(DEFAULT_CHECKLIST);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [projects, setProjects] = useState([]);
  
  // Load templates on mount
  useEffect(() => {
    if (open) {
      loadTemplates();
      loadProjects();
    }
  }, [open, flags.auditProjectSelection]);
  
  const loadProjects = async () => {
    if (!flags.auditProjectSelection) { setProjects([]); return; }
    try {
      const { data } = await axiosInstance.get('/audit-projects');
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to load selectable projects:', error);
      setProjects([]);
    }
  };
  
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axiosInstance.get('/audit-templates');
      setTemplates(response.data);
      // Set default template
      if (response.data.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load audit templates:', error);
      // Use default checklist if templates fail to load
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  // Handle template change
  const handleTemplateChange = async (templateId) => {
    setSelectedTemplateId(templateId);
    
    try {
      const response = await axiosInstance.get(`/audit-templates/${templateId}`);
      const templateChecklist = response.data.checklist;
      setActiveChecklist(templateChecklist);
      
      // Reinitialize the checklist state with new template
      const newChecklist = {};
      Object.keys(templateChecklist).forEach(categoryKey => {
        newChecklist[categoryKey] = {};
        templateChecklist[categoryKey].items.forEach(item => {
          newChecklist[categoryKey][item.key] = { status: 'na', notes: '' };
        });
      });
      setChecklist(newChecklist);
      
      toast.success(`Loaded "${response.data.name}" template`);
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template');
    }
  };
  
  // Form State
  const [basicInfo, setBasicInfo] = useState({
    auditDate: new Date().toISOString().split('T')[0],
    location: '',
    projectName: '',
    projectId: '',
    crewInvolved: '',
    workDescription: ''
  });
  
  const selectProject = (value) => {
    if (value === 'unassigned') {
      setBasicInfo((prev) => ({
        ...prev,
        projectId: '',
        projectName: '',
        // only clear the site if the project filled it in
        location: prev.locationFromProject ? '' : prev.location,
        locationFromProject: false,
      }));
      return;
    }
    const project = projects.find((p) => p.id === value);
    if (!project) return;
    setBasicInfo((prev) => {
      const takeProjectLocation = !prev.location || prev.locationFromProject;
      return {
        ...prev,
        projectId: project.id,
        projectName: project.name,
        location: takeProjectLocation ? (project.location || '') : prev.location,
        locationFromProject: takeProjectLocation && Boolean(project.location),
      };
    });
  };
  
  // Initialize checklist with all categories
  const initializeChecklist = (checklistConfig = DEFAULT_CHECKLIST) => {
    const checklist = {};
    Object.keys(checklistConfig).forEach(categoryKey => {
      checklist[categoryKey] = {};
      checklistConfig[categoryKey].items.forEach(item => {
        checklist[categoryKey][item.key] = { status: 'na', notes: '' };
      });
    });
    return checklist;
  };
  
  const [checklist, setChecklist] = useState(initializeChecklist());
  
  const [detailedInfo, setDetailedInfo] = useState({
    detailedFindings: '',
    unusualHazards: '',
    failureExplanations: ''
  });
  
  const [photos, setPhotos] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  
  const [aiResults, setAiResults] = useState(null);
  const [editableAiResults, setEditableAiResults] = useState(null);
  const [currentAuditId, setCurrentAuditId] = useState(null);
  
  const [signoff, setSignoff] = useState({
    supervisorName: user?.name || '',
    supervisorSignature: ''
  });
  
  // Handle checklist item change
  const handleChecklistChange = (category, itemKey, field, value) => {
    setChecklist(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [itemKey]: {
          ...prev[category][itemKey],
          [field]: value
        }
      }
    }));
  };
  
  // Create a draft audit for immediate photo uploads
  const createDraftAudit = async () => {
    if (draftAuditId) return draftAuditId;
    
    try {
      // Create a minimal draft audit to attach photos to
      // Include all required fields with defaults
      const draftData = {
        auditDate: basicInfo.auditDate || new Date().toISOString().split('T')[0],
        location: basicInfo.location || 'Photo Upload in Progress',
        projectId: basicInfo.projectId || null,
        crewInvolved: basicInfo.crewInvolved || 'TBD',
        checklist: {},
        detailedFindings: '',
        unusualHazards: '',
        failureExplanations: '',
        supervisorName: signoff.supervisorName || user?.name || 'TBD',
        status: 'draft',
        divisionId: effectiveDivisionId
      };
      
      console.log('Creating draft audit with data:', draftData);
      const response = await axiosInstance.post('/safety-audits', draftData);
      const auditId = response.data.id;
      setDraftAuditId(auditId);
      console.log('Draft audit created:', auditId);
      return auditId;
    } catch (error) {
      console.error('Failed to create draft audit:', error);
      console.error('Draft audit error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMsg = 'Failed to prepare photo upload';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMsg = Array.isArray(detail) ? detail[0]?.msg || errorMsg : detail;
      }
      toast.error(errorMsg);
      throw error;
    }
  };
  
  // Upload a single photo immediately with AI analysis
  const uploadPhotoImmediately = async (file, index) => {
    // Update status to uploading
    setUploadedPhotos(prev => prev.map((p, i) => 
      i === index ? { ...p, status: 'uploading' } : p
    ));
    
    try {
      // Ensure we have an audit ID
      const auditId = await createDraftAudit();
      
      // Update to analyzing status
      setUploadedPhotos(prev => prev.map((p, i) => 
        i === index ? { ...p, status: 'analyzing' } : p
      ));
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post(
        `/safety-audits/${auditId}/upload-photo`,
        formData,
        { timeout: 120000 } // 2 min timeout
      );
      
      // Update with results
      setUploadedPhotos(prev => prev.map((p, i) => 
        i === index ? {
          ...p,
          status: response.data.analysisStatus === 'success' ? 'success' : 'analyzed',
          photoUrl: response.data.photoUrl,
          analysis: response.data.analysis
        } : p
      ));
      
      if (response.data.analysisStatus === 'success') {
        toast.success(`Photo ${index + 1} analyzed!`, { duration: 2000 });
      } else {
        toast.info(`Photo ${index + 1} saved. Analysis pending.`, { duration: 2000 });
      }
      
    } catch (error) {
      console.error('Photo upload failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      });
      
      let errorMessage = 'Upload failed';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out - try a smaller photo';
      } else if (error.response?.status === 413) {
        errorMessage = 'Photo too large';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied';
      } else if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Server error';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection';
      }
      
      setUploadedPhotos(prev => prev.map((p, i) => 
        i === index ? { ...p, status: 'failed', error: errorMessage } : p
      ));
      toast.error(`Photo ${index + 1}: ${errorMessage}`);
    }
  };
  
  // Handle photo selection - uploads immediately
  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Add new photos to state with pending status
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      analysis: null,
      photoUrl: null
    }));
    
    const startIndex = uploadedPhotos.length;
    setUploadedPhotos(prev => [...prev, ...newPhotos]);
    
    // Also keep legacy state for backward compatibility
    setPhotoFiles(prev => [...prev, ...files]);
    setPhotos(prev => [...prev, ...newPhotos.map(p => p.preview)]);
    
    // Upload each photo immediately
    for (let i = 0; i < files.length; i++) {
      // Don't await - let them upload in parallel
      uploadPhotoImmediately(files[i], startIndex + i);
    }
  };
  
  // Remove a photo
  const handleRemovePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };
  
  // Retry failed photo analysis
  const handleRetryPhoto = async (index) => {
    const photo = uploadedPhotos[index];
    if (!photo) return;
    
    if (photo.photoUrl && draftAuditId) {
      // Photo was uploaded but analysis failed - use retry endpoint
      setUploadedPhotos(prev => prev.map((p, i) => 
        i === index ? { ...p, status: 'analyzing' } : p
      ));
      
      try {
        const response = await axiosInstance.post(
          `/safety-audits/${draftAuditId}/retry-photo-analysis`,
          null,
          { params: { photo_url: photo.photoUrl }, timeout: 120000 }
        );
        
        setUploadedPhotos(prev => prev.map((p, i) => 
          i === index ? { ...p, status: 'success', analysis: response.data.analysis } : p
        ));
        toast.success(`Photo ${index + 1} analyzed!`);
      } catch (error) {
        setUploadedPhotos(prev => prev.map((p, i) => 
          i === index ? { ...p, status: 'failed' } : p
        ));
        toast.error('Retry failed. Please try again.');
      }
    } else {
      // Re-upload the photo
      uploadPhotoImmediately(photo.file, index);
    }
  };
  
  // Handle AI results editing
  const handleAddItem = (field) => {
    setEditableAiResults(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };
  
  const handleUpdateItem = (field, index, value) => {
    setEditableAiResults(prev => ({
      ...prev,
      [field]: prev[field].map((item, idx) => idx === index ? value : item)
    }));
  };
  
  const handleDeleteItem = (field, index) => {
    setEditableAiResults(prev => ({
      ...prev,
      [field]: prev[field].filter((_, idx) => idx !== index)
    }));
  };
  
  const handleUpdateHazardSummary = (value) => {
    setEditableAiResults(prev => ({
      ...prev,
      aiHazardSummary: value
    }));
  };
  
  // Save edited AI results
  const handleSaveAiResults = async () => {
    try {
      // Reset states to allow close
      setSubmitting(false);
      setAnalyzingAudit(false);
      
      await axiosInstance.put(`/safety-audits/${currentAuditId}`, {
        aiMissingInfo: editableAiResults.aiMissingInfo.filter(item => item.trim() !== ''),
        aiHazardSummary: editableAiResults.aiHazardSummary,
        aiCorrectiveActions: editableAiResults.aiCorrectiveActions.filter(item => item.trim() !== ''),
        aiRiskScore: editableAiResults.aiRiskScore,
        aiSafetyScore: editableAiResults.aiSafetyScore,
        aiTrainingRecommendations: editableAiResults.aiTrainingRecommendations.filter(item => item.trim() !== ''),
        aiJsaUpdates: editableAiResults.aiJsaUpdates.filter(item => item.trim() !== ''),
        status: 'completed'  // Mark audit as completed to trigger JSA recommendations generation
      });
      
      toast.success('Audit completed! Generating JSA recommendations...');
      console.log('✅ Audit marked as completed - triggering JSA recommendations generation');
      console.log(`   Audit ID: ${currentAuditId}`);
      console.log(`   Location: ${basicInfo.location}`);
      
      // Wait a moment for backend to generate recommendations, then fetch them
      await new Promise(resolve => setTimeout(resolve, 3000)); // Give backend 3 seconds
      
      try {
        const recResponse = await axiosInstance.get(`/audits/${currentAuditId}/jsa-recommendations`);
        console.log('📥 JSA Recommendations check response:', recResponse.data);
        
        if (recResponse.data.recommendations && recResponse.data.recommendations.length > 0) {
          const recCount = recResponse.data.recommendations[0]?.recommendations?.length || 0;
          if (recCount > 0) {
            toast.success(`✅ ${recCount} JSA recommendation${recCount > 1 ? 's' : ''} generated!`);
          } else {
            console.log('⚠️ Recommendations document exists but has no recommendations');
          }
        } else {
          console.log('ℹ️ No JSA recommendations generated. This could mean:');
          console.log('   - No JSAs exist at location "' + basicInfo.location + '"');
          console.log('   - JSAs exist but don\'t match the audit findings');
          console.log('   - Backend generation failed (check backend logs)');
        }
      } catch (error) {
        console.log('⚠️ Error fetching JSA recommendations:', error.response?.status, error.response?.data);
        if (error.response?.status === 404) {
          console.log('   No recommendations found. Recommendations will be available when viewing this audit if generation succeeded.');
        }
      }
      
      // Close dialog (handleClose will call onSuccess to refresh the list)
      handleClose();
    } catch (error) {
      console.error('Failed to update AI results:', error);
      toast.error('Failed to update AI results');
    }
  };
  
  // Submit audit
  const handleSubmit = async () => {
    try {
      // Validation
      if (!basicInfo.location || !basicInfo.crewInvolved) {
        toast.error('Please fill in location and crew information');
        return;
      }
      
      setSubmitting(true);
      
      // Build audit data
      const auditData = {
        ...basicInfo,
        checklist: checklist,
        ...detailedInfo,
        supervisorName: signoff.supervisorName,
        supervisorSignature: signoff.supervisorSignature,
        divisionId: effectiveDivisionId,
        companyId: user?.companyId
      };
      
      // Debug logging
      console.log('📤 Submitting audit...');
      console.log('📋 Checklist data:', JSON.stringify(checklist).slice(0, 500));
      console.log('📝 Basic info:', basicInfo);
      console.log('🔍 Detailed info:', detailedInfo);
      console.log('🌐 Online status:', isOnline);
      
      // Count checklist items
      let checklistItemCount = 0;
      Object.keys(checklist).forEach(cat => {
        Object.keys(checklist[cat] || {}).forEach(item => {
          if (checklist[cat][item]?.status !== 'na') {
            checklistItemCount++;
          }
        });
      });
      console.log(`📊 Checklist items (non-N/A): ${checklistItemCount}`);
      
      // OFFLINE MODE: Queue audit for sync later
      if (!isOnline) {
        console.log('📴 Offline mode - queuing audit for sync');
        const offlineId = generateId();
        const offlineAudit = {
          ...auditData,
          id: offlineId,
          status: 'pending_sync',
          createdAt: new Date().toISOString(),
          offlineCreated: true
        };
        
        // Save to local IndexedDB
        await saveToStore('audits', offlineAudit);
        
        // Queue for background sync
        await queueSafetyAudit(auditData);
        await updatePendingCount();
        
        toast.success('Audit saved offline! Will sync when connected.', {
          description: 'Your audit has been queued and will be uploaded automatically.',
          icon: <WifiOff className="h-4 w-4" />
        });
        
        // Close dialog (handleClose will call onSuccess to refresh the list)
        handleClose();
        setSubmitting(false);
        return;
      }
      
      // ONLINE MODE: Submit to server
      let auditId = draftAuditId;
      
      // If we have a draft audit (photos were uploaded), update it
      // Otherwise create a new audit
      if (draftAuditId) {
        // Update the draft audit with full data
        auditData.status = 'pending'; // Change from draft to pending
        
        console.log('📤 Updating draft audit:', draftAuditId);
        const updateResponse = await axiosInstance.put(`/safety-audits/${draftAuditId}`, auditData);
        console.log('✅ Audit updated:', updateResponse.data?.id || 'success');
        toast.success('Audit updated successfully');
      } else {
        // Create new audit (no photos were uploaded)
        console.log('📤 Creating new audit...');
        const response = await axiosInstance.post('/safety-audits', auditData);
        auditId = response.data.id;
        console.log('✅ Audit created:', auditId);
        toast.success('Audit created successfully');
      }
      
      setCurrentAuditId(auditId);
      
      // Photos are already uploaded - just show summary
      const analyzedPhotos = uploadedPhotos.filter(p => p.status === 'success');
      if (analyzedPhotos.length > 0) {
        toast.info(`${analyzedPhotos.length} photo(s) already analyzed and attached.`);
      }
      
      // Move to AI Analysis tab
      setCurrentTab('ai');
      
      // Run AI analysis with timeout
      setAnalyzingAudit(true);
      toast.info('Running AI analysis (this may take up to 30 seconds)...');
      
      try {
        const aiResponse = await axiosInstance.post(`/safety-audits/${auditId}/analyze`, null, {
          timeout: 60000 // 1 minute timeout for AI analysis
        });
        setAiResults(aiResponse.data);
        // Create editable copy of AI results
        setEditableAiResults({
          aiMissingInfo: [...(aiResponse.data.aiMissingInfo || [])],
          aiHazardSummary: aiResponse.data.aiHazardSummary || '',
          aiCorrectiveActions: [...(aiResponse.data.aiCorrectiveActions || [])],
          aiRiskScore: aiResponse.data.aiRiskScore || '',
          aiSafetyScore: aiResponse.data.aiSafetyScore || 0,
          aiTrainingRecommendations: [...(aiResponse.data.aiTrainingRecommendations || [])],
          aiJsaUpdates: [...(aiResponse.data.aiJsaUpdates || [])]
        });
        toast.success('AI analysis complete! You can now edit the results.');
      } catch (aiError) {
        console.error('AI analysis failed:', aiError);
        // The analysis often outlives the request. Give it a chance to land before calling it a
        // failure, so nobody re-runs an analysis that actually succeeded.
        toast.info('Still analysing — waiting for the result...');
        const settled = await waitForAuditAnalysis(auditId);
        if (settled) {
          setAiResults(settled);
          setEditableAiResults({
            aiMissingInfo: [...(settled.aiMissingInfo || [])],
            aiHazardSummary: settled.aiHazardSummary || '',
            aiCorrectiveActions: [...(settled.aiCorrectiveActions || [])],
            aiRiskScore: settled.aiRiskScore || '',
            aiSafetyScore: settled.aiSafetyScore || 0,
            aiTrainingRecommendations: [...(settled.aiTrainingRecommendations || [])],
            aiJsaUpdates: [...(settled.aiJsaUpdates || [])]
          });
          toast.success('AI analysis complete! You can now edit the results.');
          setAnalyzingAudit(false);
          return;
        }
        const errorMessage = aiError.code === 'ECONNABORTED' 
          ? 'AI analysis timed out. The audit was saved - you can retry analysis later.'
          : 'AI analysis failed. The audit was saved - you can retry analysis later.';
        toast.warning(errorMessage);
        // Set empty results so user can still proceed
        setAiResults({});
        setEditableAiResults({
          aiMissingInfo: [],
          aiHazardSummary: '',
          aiCorrectiveActions: [],
          aiRiskScore: 'Pending',
          aiSafetyScore: 0,
          aiTrainingRecommendations: [],
          aiJsaUpdates: []
        });
      }
      setAnalyzingAudit(false);
      
      // Fetch JSA recommendations
      try {
        setLoadingRecommendations(true);
        const recResponse = await axiosInstance.get(`/audits/${auditId}/jsa-recommendations`);
        setJsaRecommendations(recResponse.data);
        toast.success('JSA recommendations generated!');
      } catch (error) {
        console.error('Failed to load JSA recommendations:', error);
        // Don't show error - recommendations might not exist yet
      } finally {
        setLoadingRecommendations(false);
      }
      
      // Do NOT call onSuccess() here - keep dialog open so user can review/edit AI results
      // onSuccess will be called when the dialog closes (via handleClose)
      
      // Reset submission states
      setSubmitting(false);
      setAnalyzingAudit(false);
      
    } catch (error) {
      console.error('Failed to submit audit:', error);
      toast.error('Failed to submit audit: ' + (error.response?.data?.detail || error.message));
      setSubmitting(false);
      setAnalyzingAudit(false);
    }
  };
  
  // Close and reset
  const handleClose = () => {
    // Always allow closing
    // If an audit was created/modified, refresh the parent list
    const shouldRefresh = !!currentAuditId || !!draftAuditId;
    
    // Reset form
    setBasicInfo({
      auditDate: new Date().toISOString().split('T')[0],
      location: '',
      crewInvolved: '',
      workDescription: ''
    });
    setChecklist(initializeChecklist());
    setDetailedInfo({
      detailedFindings: '',
      unusualHazards: '',
      failureExplanations: ''
    });
    setPhotos([]);
    setPhotoFiles([]);
    setUploadedPhotos([]); // Reset new photo state
    setDraftAuditId(null); // Reset draft audit
    setAiResults(null);
    setEditableAiResults(null);
    setCurrentAuditId(null);
    setSignoff({
      supervisorName: user?.name || '',
      supervisorSignature: ''
    });
    setCurrentTab('basic');
    setSubmitting(false);
    setAnalyzingAudit(false);
    
    // Refresh parent list if audit was created
    if (shouldRefresh && onSuccess) {
      onSuccess();
    } else {
      onOpenChange(false);
    }
  };
  
  // Calculate completion stats
  const getChecklistStats = () => {
    let total = 0;
    let pass = 0;
    let fail = 0;
    let na = 0;
    
    Object.values(checklist).forEach(category => {
      Object.values(category).forEach(item => {
        total++;
        if (item.status === 'pass') pass++;
        else if (item.status === 'fail') fail++;
        else na++;
      });
    });
    
    return { total, pass, fail, na };
  };
  
  const stats = getChecklistStats();
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <CheckSquare className="w-6 h-6 text-red-600" />
            Comprehensive Safety Audit
          </DialogTitle>
          <DialogDescription>
            Complete all sections for AI-powered hazard analysis and safety recommendations
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">1. Basic Info</TabsTrigger>
            <TabsTrigger value="checklist">2. Checklist</TabsTrigger>
            <TabsTrigger value="details">3. Details</TabsTrigger>
            <TabsTrigger value="ai" disabled={!aiResults && !analyzingAudit}>4. AI Analysis</TabsTrigger>
          </TabsList>
          
          {/* Tab 1: Basic Information */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Audit Information</CardTitle>
                <CardDescription>Basic details about this safety audit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Template Selection */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label htmlFor="template" className="text-blue-900 font-semibold">{t('auditDialog.auditTemplate')} *</Label>
                  <Select 
                    value={selectedTemplateId} 
                    onValueChange={handleTemplateChange}
                    disabled={loadingTemplates}
                  >
                    <SelectTrigger className="mt-2 bg-white">
                      <SelectValue placeholder={loadingTemplates ? "Loading templates..." : "Select audit type"} />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <span className="flex items-center gap-2">
                            <span>{template.icon || '📋'}</span>
                            <span>{template.name}</span>
                            <Badge variant="outline" className="ml-2 text-xs">
                              {template.itemCount} items
                            </Badge>
                            {template.isSystem && (
                              <Badge variant="secondary" className="text-xs">System</Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-700 mt-2">
                    Select the type of audit you want to perform. This determines the checklist items.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="auditDate">{t('auditDialog.auditDate')} *</Label>
                    <Input
                      id="auditDate"
                      type="date"
                      value={basicInfo.auditDate}
                      onChange={(e) => setBasicInfo({...basicInfo, auditDate: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location/Site *</Label>
                    <Input
                      id="location"
                      placeholder="e.g., Construction Site A, Building 5"
                      value={basicInfo.location}
                      onChange={(e) => setBasicInfo({...basicInfo, location: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="crew">{t('auditDialog.crewInvolved')} *</Label>
                    <Input
                      id="crew"
                      data-testid="audit-crew-input"
                      placeholder="e.g., Framing Crew, Electrical Team"
                      value={basicInfo.crewInvolved}
                      onChange={(e) => setBasicInfo({...basicInfo, crewInvolved: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="projectName">{t('audits.project')}</Label>
                    {projects.length > 0 ? (
                      <>
                        <Select value={basicInfo.projectId || 'unassigned'}
                                onValueChange={selectProject}>
                          <SelectTrigger id="projectName" data-testid="audit-project-select">
                            <SelectValue placeholder={t('audits.selectProject')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned" data-testid="audit-project-unassigned">
                              {t('audits.noProjectUnassigned')}
                            </SelectItem>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}
                                          data-testid={`audit-project-option-${project.id}`}>
                                {project.name}
                                {project.location ? ` - ${project.location}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1" data-testid="audit-project-hint">
                          {t('audits.projectHint')}
                        </p>
                      </>
                    ) : (
                      <>
                        <Input
                          id="projectName"
                          data-testid="audit-project-input"
                          placeholder="e.g., Whirlpool Plant Expansion, Job #4821"
                          value={basicInfo.projectName}
                          onChange={(e) => setBasicInfo({...basicInfo, projectName: e.target.value})}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {t('audits.noProjectsTypeName')}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="workDescription">{t('auditDialog.workDescription')}</Label>
                  <Textarea
                    id="workDescription"
                    placeholder="Describe the work being performed (e.g., Installing electrical conduit, Framing second floor walls, Pouring concrete foundation)"
                    value={basicInfo.workDescription}
                    onChange={(e) => setBasicInfo({...basicInfo, workDescription: e.target.value})}
                    rows={3}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Provide a brief description of what workers are doing during this audit
                  </p>
                </div>
                
                <div className="flex justify-end mt-6">
                  <Button onClick={() => setCurrentTab('checklist')}>
                    Next: Safety Checklist →
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Tab 2: Safety Checklist */}
          <TabsContent value="checklist" className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-4">
                <CheckSquare className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900">Checklist Progress</h3>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span>Pass: {stats.pass}</span>
                    <span>Fail: {stats.fail}</span>
                    <span>N/A: {stats.na}</span>
                    <span className="font-semibold">Total: {stats.total}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              {Object.entries(activeChecklist).map(([categoryKey, category]) => (
                <Card key={categoryKey}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {category.items.map((item) => {
                      const itemValue = checklist[categoryKey]?.[item.key] || { status: 'na', notes: '' };
                      return (
                        <div key={item.key} className="border-b pb-3 last:border-b-0">
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <Label className="text-sm font-normal">{item.label}</Label>
                              <RadioGroup
                                value={itemValue.status}
                                onValueChange={(value) => handleChecklistChange(categoryKey, item.key, 'status', value)}
                                className="flex gap-6 mt-2"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="pass" id={`${categoryKey}-${item.key}-pass`} />
                                  <Label htmlFor={`${categoryKey}-${item.key}-pass`} className="cursor-pointer font-normal">
                                    Pass
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="fail" id={`${categoryKey}-${item.key}-fail`} />
                                  <Label htmlFor={`${categoryKey}-${item.key}-fail`} className="cursor-pointer font-normal">
                                    Fail
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="na" id={`${categoryKey}-${item.key}-na`} />
                                  <Label htmlFor={`${categoryKey}-${item.key}-na`} className="cursor-pointer font-normal">
                                    N/A
                                  </Label>
                                </div>
                              </RadioGroup>
                              {itemValue.status === 'fail' && (
                                <Input
                                  placeholder="Add notes about this failure..."
                                  value={itemValue.notes}
                                  onChange={(e) => handleChecklistChange(categoryKey, item.key, 'notes', e.target.value)}
                                  className="mt-2 text-sm"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrentTab('basic')}>← Back</Button>
              <Button onClick={() => setCurrentTab('details')}>{t('auditDialog.nextDetailedFindings')} →</Button>
            </div>
          </TabsContent>
          
          {/* Tab 3: Detailed Findings */}
          <TabsContent value="details" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Detailed Findings & Observations
                </CardTitle>
                <CardDescription>{t('auditDialog.provideDetails')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="findings">Detailed Findings</Label>
                  <Textarea
                    id="findings"
                    placeholder="Describe overall site conditions, safety concerns, worker behavior, equipment status, etc..."
                    value={detailedInfo.detailedFindings}
                    onChange={(e) => setDetailedInfo({...detailedInfo, detailedFindings: e.target.value})}
                    rows={5}
                  />
                </div>
                
                <div>
                  <Label htmlFor="hazards">{t('auditDialog.unusualHazards')}</Label>
                  <Textarea
                    id="hazards"
                    placeholder="Any unusual or unexpected hazards observed?"
                    value={detailedInfo.unusualHazards}
                    onChange={(e) => setDetailedInfo({...detailedInfo, unusualHazards: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="explanations">{t('auditDialog.failureExplanations')}</Label>
                  <Textarea
                    id="explanations"
                    placeholder="Explain any checklist failures and their context..."
                    value={detailedInfo.failureExplanations}
                    onChange={(e) => setDetailedInfo({...detailedInfo, failureExplanations: e.target.value})}
                    rows={3}
                  />
                </div>
                
                {/* Photo Upload Section - Now with immediate upload & analysis */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <Label htmlFor="photos" className="flex items-center gap-2 text-base font-semibold mb-2">
                    <Upload className="w-5 h-5" />
                    Photo Evidence (AI-Analyzed Instantly)
                  </Label>
                  <p className="text-sm text-gray-600 mb-3">
                    📸 Photos are uploaded and analyzed immediately. Results appear below.
                  </p>
                  <Input
                    id="photos"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="mb-3"
                  />
                  
                  {/* Photo Grid with Status */}
                  {uploadedPhotos.length > 0 && (
                    <div className="space-y-3 mt-4">
                      {uploadedPhotos.map((photo, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                          {/* Thumbnail */}
                          <div className="relative w-24 h-24 flex-shrink-0">
                            <img 
                              src={photo.preview} 
                              alt={`Photo ${idx + 1}`} 
                              className="w-full h-full object-cover rounded"
                            />
                            {/* Status overlay */}
                            {(photo.status === 'uploading' || photo.status === 'analyzing') && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded">
                                <Loader2 className="w-6 h-6 text-white animate-spin" />
                              </div>
                            )}
                            {photo.status === 'success' && (
                              <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                                <CheckCircle className="w-4 h-4 text-white" />
                              </div>
                            )}
                            {photo.status === 'failed' && (
                              <div className="absolute top-1 right-1 bg-red-500 rounded-full p-0.5">
                                <X className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                          
                          {/* Analysis Results */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">Photo {idx + 1}</span>
                              <div className="flex items-center gap-2">
                                {photo.status === 'failed' && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleRetryPhoto(idx)}
                                    className="text-xs h-7"
                                  >
                                    Retry
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleRemovePhoto(idx)}
                                  className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Status Messages */}
                            {photo.status === 'pending' && (
                              <p className="text-xs text-gray-500">Waiting to upload...</p>
                            )}
                            {photo.status === 'uploading' && (
                              <p className="text-xs text-blue-600">Uploading...</p>
                            )}
                            {photo.status === 'analyzing' && (
                              <p className="text-xs text-purple-600">🤖 AI analyzing for hazards...</p>
                            )}
                            {photo.status === 'failed' && (
                              <p className="text-xs text-red-600">Upload failed - click retry</p>
                            )}
                            
                            {/* Analysis Results */}
                            {photo.status === 'success' && photo.analysis && (
                              <div className="space-y-1">
                                <div className="flex gap-2 flex-wrap">
                                  <Badge className={`text-xs ${
                                    photo.analysis.overallSafetyScore >= 80 ? 'bg-green-600' :
                                    photo.analysis.overallSafetyScore >= 60 ? 'bg-orange-500' :
                                    'bg-red-600'
                                  }`}>
                                    Safety: {photo.analysis.overallSafetyScore}/100
                                  </Badge>
                                  <Badge className={`text-xs ${
                                    photo.analysis.overallErgonomicsScore >= 80 ? 'bg-green-600' :
                                    photo.analysis.overallErgonomicsScore >= 60 ? 'bg-orange-500' :
                                    'bg-red-600'
                                  }`}>
                                    Ergonomics: {photo.analysis.overallErgonomicsScore}/100
                                  </Badge>
                                  <Badge className={`text-xs ${
                                    photo.analysis.riskLevel === 'Low' ? 'bg-green-600' :
                                    photo.analysis.riskLevel === 'Medium' ? 'bg-orange-500' :
                                    'bg-red-600'
                                  }`}>
                                    Risk: {photo.analysis.riskLevel}
                                  </Badge>
                                </div>
                                
                                {/* Quick findings summary */}
                                {photo.analysis.hazardSummary && (
                                  <p className="text-xs text-gray-600 line-clamp-2">
                                    {photo.analysis.hazardSummary}
                                  </p>
                                )}
                                
                                {/* Immediate actions if any */}
                                {photo.analysis.immediateActions?.length > 0 && (
                                  <div className="mt-1 p-2 bg-red-50 rounded text-xs">
                                    <span className="font-semibold text-red-700">⚠️ Action needed: </span>
                                    <span className="text-red-600">{photo.analysis.immediateActions[0]}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Analyzed but no detailed results */}
                            {photo.status === 'analyzed' && !photo.analysis && (
                              <p className="text-xs text-gray-500">Photo saved. AI analysis pending.</p>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Summary of all photos */}
                      {uploadedPhotos.filter(p => p.status === 'success').length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Photo Analysis Summary
                          </p>
                          <div className="mt-2 text-xs text-blue-700">
                            <p>✓ {uploadedPhotos.filter(p => p.status === 'success').length} photo(s) analyzed</p>
                            {uploadedPhotos.some(p => p.analysis?.riskLevel === 'High') && (
                              <p className="text-red-600 font-semibold">⚠️ High-risk hazards detected in photos!</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Supervisor Sign-off
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="supervisorName">{t('auditDialog.supervisorName')} *</Label>
                      <Input
                        id="supervisorName"
                        value={signoff.supervisorName}
                        onChange={(e) => setSignoff({...signoff, supervisorName: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="signature">{t('auditDialog.signature')} *</Label>
                      <Input
                        id="signature"
                        placeholder="Type your full name to sign"
                        value={signoff.supervisorSignature}
                        onChange={(e) => setSignoff({...signoff, supervisorSignature: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrentTab('checklist')}>← Back</Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting || !basicInfo.location || !signoff.supervisorSignature}
                className="bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  <>{t('auditDialog.submitAnalyzeAi')}</>
                )}
              </Button>
            </div>
          </TabsContent>
          
          {/* Tab 4: AI Analysis Results */}
          <TabsContent value="ai" className="space-y-4 mt-4">
            {analyzingAudit ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-600" />
                  <h3 className="text-xl font-semibold mb-2">Analyzing Safety Audit...</h3>
                  <p className="text-gray-600">GPT-4o is reviewing your checklist, findings, and photos</p>
                </CardContent>
              </Card>
            ) : aiResults ? (
              <div className="space-y-4">
                {/* AI Header */}
                <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-900">
                      <Brain className="w-6 h-6" />
                      AI Analysis Results (GPT-4o)
                    </CardTitle>
                    <CardDescription>Comprehensive safety analysis and recommendations</CardDescription>
                  </CardHeader>
                </Card>
                
                {/* Official figures — calculated from the recorded checklist, not AI-generated */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">{t('audits.officialRisk')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          !aiResults.calculatedScoring ? 'bg-gray-400' :
                          ['HIGH', 'CRITICAL'].includes(aiResults.calculatedScoring.risk_level) ? 'bg-red-600' :
                          aiResults.calculatedScoring.risk_level === 'MODERATE' ? 'bg-orange-500' :
                          'bg-green-600'
                        } data-testid="audit-dialog-official-risk">
                          {aiResults.calculatedScoring?.risk_level || t('audits.notScoredYet')}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">{t('audits.complianceRate')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" data-testid="audit-dialog-compliance">
                        {aiResults.calculatedScoring?.applicable_items
                          ? `${aiResults.calculatedScoring.compliance_rate}%`
                          : 'N/A'}
                      </div>
                      {aiResults.calculatedScoring?.applicable_items > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          {aiResults.calculatedScoring.passed_checks} / {aiResults.calculatedScoring.applicable_items}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Photo Analysis Summary - NEW SECTION */}
                {uploadedPhotos.filter(p => p.status === 'success' && p.analysis).length > 0 && (
                  <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2 text-purple-900">
                        <Eye className="w-5 h-5" />
                        Photo Evidence Summary
                        <Badge className="ml-2 bg-purple-600">
                          {uploadedPhotos.filter(p => p.status === 'success').length} Photos Analyzed
                        </Badge>
                      </CardTitle>
                      <CardDescription>AI analysis findings from uploaded photos</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Aggregate Scores */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <p className="text-xs text-gray-500 mb-1">Avg Safety Score</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {Math.round(
                              uploadedPhotos
                                .filter(p => p.analysis?.overallSafetyScore)
                                .reduce((acc, p) => acc + p.analysis.overallSafetyScore, 0) /
                              uploadedPhotos.filter(p => p.analysis?.overallSafetyScore).length || 0
                            )}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <p className="text-xs text-gray-500 mb-1">Avg Ergonomics</p>
                          <p className="text-2xl font-bold text-green-600">
                            {Math.round(
                              uploadedPhotos
                                .filter(p => p.analysis?.overallErgonomicsScore)
                                .reduce((acc, p) => acc + p.analysis.overallErgonomicsScore, 0) /
                              uploadedPhotos.filter(p => p.analysis?.overallErgonomicsScore).length || 0
                            )}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <p className="text-xs text-gray-500 mb-1">Highest Risk</p>
                          <Badge className={`text-sm ${
                            uploadedPhotos.some(p => p.analysis?.riskLevel === 'High') ? 'bg-red-600' :
                            uploadedPhotos.some(p => p.analysis?.riskLevel === 'Medium') ? 'bg-orange-500' :
                            'bg-green-600'
                          }`}>
                            {uploadedPhotos.some(p => p.analysis?.riskLevel === 'High') ? 'High' :
                             uploadedPhotos.some(p => p.analysis?.riskLevel === 'Medium') ? 'Medium' : 'Low'}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Key Findings from Photos */}
                      <div>
                        <h4 className="font-semibold text-sm mb-2 text-purple-800">Key Findings from Photos:</h4>
                        <div className="space-y-2">
                          {uploadedPhotos
                            .filter(p => p.analysis?.hazardSummary)
                            .map((photo, idx) => (
                              <div key={idx} className="flex gap-3 p-2 bg-white rounded border">
                                <img src={photo.preview} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 mb-0.5">Photo {idx + 1}</p>
                                  <p className="text-sm line-clamp-2">{photo.analysis.hazardSummary}</p>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                      
                      {/* Immediate Actions from Photos */}
                      {uploadedPhotos.some(p => p.analysis?.immediateActions?.length > 0) && (
                        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                          <h4 className="font-semibold text-sm mb-2 text-red-800 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Immediate Actions Required (from Photos)
                          </h4>
                          <ul className="text-sm space-y-1">
                            {uploadedPhotos
                              .filter(p => p.analysis?.immediateActions?.length > 0)
                              .flatMap((p, idx) => p.analysis.immediateActions.map((action, aIdx) => (
                                <li key={`${idx}-${aIdx}`} className="flex items-start gap-2">
                                  <span className="text-red-600">•</span>
                                  <span>{action}</span>
                                </li>
                              )))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Missing Information */}
                {editableAiResults && editableAiResults.aiMissingInfo.length > 0 && (
                  <Card className="border-orange-200">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        <div>
                          <CardTitle className="text-base">Missing Information</CardTitle>
                          <CardDescription>Edit or remove missing information items</CardDescription>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddItem('aiMissingInfo')}
                      >
                        + Add Item
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {editableAiResults.aiMissingInfo.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={item}
                              onChange={(e) => handleUpdateItem('aiMissingInfo', idx, e.target.value)}
                              className="flex-1"
                              placeholder="Enter missing information..."
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteItem('aiMissingInfo', idx)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Hazard Summary */}
                {editableAiResults && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Hazard Summary</CardTitle>
                      <CardDescription>Edit the AI-generated hazard summary</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={editableAiResults.aiHazardSummary}
                        onChange={(e) => handleUpdateHazardSummary(e.target.value)}
                        rows={4}
                        className="text-sm"
                        placeholder="Add hazard summary..."
                      />
                    </CardContent>
                  </Card>
                )}
                
                {/* Corrective Actions */}
                {editableAiResults && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Corrective Actions Required</CardTitle>
                        <CardDescription>Add, edit, or remove corrective actions</CardDescription>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddItem('aiCorrectiveActions')}
                      >
                        + Add Action
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {editableAiResults.aiCorrectiveActions.map((action, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="inline-block w-6 h-6 bg-red-100 text-red-700 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-2">
                              {idx + 1}
                            </span>
                            <Input
                              value={action}
                              onChange={(e) => handleUpdateItem('aiCorrectiveActions', idx, e.target.value)}
                              className="flex-1"
                              placeholder="Enter corrective action..."
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteItem('aiCorrectiveActions', idx)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                        {editableAiResults.aiCorrectiveActions.length === 0 && (
                          <p className="text-sm text-gray-500 italic">No corrective actions. Click "Add Action" to add one.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Training Recommendations */}
                {editableAiResults && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Training Recommendations</CardTitle>
                        <CardDescription>Add, edit, or remove training recommendations</CardDescription>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddItem('aiTrainingRecommendations')}
                      >
                        + Add Recommendation
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {editableAiResults.aiTrainingRecommendations.map((rec, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={rec}
                              onChange={(e) => handleUpdateItem('aiTrainingRecommendations', idx, e.target.value)}
                              className="flex-1"
                              placeholder="Enter training recommendation..."
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteItem('aiTrainingRecommendations', idx)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                        {editableAiResults.aiTrainingRecommendations.length === 0 && (
                          <p className="text-sm text-gray-500 italic">No training recommendations. Click "Add Recommendation" to add one.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* JSA Updates */}
                {editableAiResults && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-base">JSA Updates Needed</CardTitle>
                        <CardDescription>Add, edit, or remove JSA updates</CardDescription>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddItem('aiJsaUpdates')}
                      >
                        + Add JSA Update
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {editableAiResults.aiJsaUpdates.map((update, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={update}
                              onChange={(e) => handleUpdateItem('aiJsaUpdates', idx, e.target.value)}
                              className="flex-1"
                              placeholder="Enter JSA update..."
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteItem('aiJsaUpdates', idx)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                        {editableAiResults.aiJsaUpdates.length === 0 && (
                          <p className="text-sm text-gray-500 italic">No JSA updates. Click "Add JSA Update" to add one.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* AI JSA Recommendations */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-900">
                      <Lightbulb className="w-5 h-5" />
                      AI JSA Recommendations
                    </CardTitle>
                    <CardDescription>Specific recommendations to improve JSAs at this location</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingRecommendations ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Loading JSA recommendations...</span>
                      </div>
                    ) : jsaRecommendations && jsaRecommendations.recommendations?.length > 0 ? (
                      <div>
                        <p className="text-sm text-gray-600 mb-3">
                          Based on this audit's findings, here are {jsaRecommendations.recommendations.length} AI-generated recommendations to improve JSAs:
                        </p>
                        <div className="space-y-3">
                          {jsaRecommendations.recommendations.map((rec, idx) => (
                            <div key={idx} className="border rounded-lg p-3 bg-white">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className={`${
                                      rec.priority === 'High' ? 'bg-red-600' :
                                      rec.priority === 'Medium' ? 'bg-orange-500' :
                                      'bg-blue-600'
                                    }`}>
                                      {rec.priority}
                                    </Badge>
                                    <span className="text-xs font-medium text-gray-600 uppercase">
                                      {rec.recommendationType?.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-gray-900 mb-1">
                                    {rec.recommendation}
                                  </p>
                                  {rec.reason && (
                                    <p className="text-xs text-gray-500 italic">
                                      Reason: {rec.reason}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {rec.jsaId && (
                                <div className="text-xs text-gray-400 mt-2">
                                  JSA ID: {rec.jsaId.substring(0, 8)}...
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-3 italic">
                          Generated: {new Date(jsaRecommendations.generatedAt).toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 bg-white p-4 rounded border border-blue-200">
                        <p className="font-medium mb-2">💡 How to get JSA recommendations:</p>
                        <ol className="list-decimal list-inside space-y-1 text-sm">
                          <li>Ensure there are existing JSAs at this location ("{basicInfo.location}")</li>
                          <li>Click "Save Changes & Close" below to complete this audit</li>
                          <li>View the audit from the dashboard to see AI-generated JSA recommendations</li>
                        </ol>
                        <p className="mt-3 text-xs text-gray-500 italic">
                          Recommendations are generated automatically when you save and complete this audit.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* JSA Compliance Check (Phase 2) */}
                {aiResults?.jsaComplianceCheck && (
                  <Card className="border-purple-200 bg-purple-50">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-purple-600" />
                        JSA Compliance Check
                      </CardTitle>
                      <CardDescription>Audit findings compared against relevant JSAs</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {!aiResults.jsaComplianceCheck.jsas_found ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-yellow-900">No Matching JSA Found</p>
                              <p className="text-sm text-yellow-700 mt-1">
                                {aiResults.jsaComplianceCheck.message}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Overall Compliance Badge */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">Overall Compliance:</span>
                            <Badge className={
                              aiResults.jsaComplianceCheck.overall_compliance === 'COMPLIANT' ? 'bg-green-500' :
                              aiResults.jsaComplianceCheck.overall_compliance === 'PARTIAL' ? 'bg-orange-500' :
                              'bg-red-500'
                            }>
                              {aiResults.jsaComplianceCheck.overall_compliance}
                            </Badge>
                          </div>

                          {/* Matched JSAs */}
                          {aiResults.jsaComplianceCheck.matched_jsas.map((jsa, idx) => (
                            <div key={idx} className="border rounded-lg p-4 bg-white">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">{jsa.jsa_title}</h4>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        // Navigate to JSA view
                                        window.open(`/jsa?highlight=${jsa.jsa_id}`, '_blank');
                                      }}
                                      className="h-6 px-2 text-xs"
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      View JSA
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={async () => {
                                        if (window.confirm(`Remove this JSA link? This won't delete the JSA, just removes it from this audit.`)) {
                                          try {
                                            await axiosInstance.delete(`/safety-audits/${currentAuditId}/linked-jsas/${jsa.jsa_id}`);
                                            toast.success('JSA link removed');
                                            // Refresh by calling onSuccess
                                            if (onSuccess) onSuccess();
                                          } catch (error) {
                                            console.error('Error removing JSA:', error);
                                            toast.error('Failed to remove JSA link');
                                          }
                                        }
                                      }}
                                      className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Remove
                                    </Button>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Match Confidence: {jsa.match_confidence}% ({jsa.match_reasons.join(', ')})
                                  </p>
                                </div>
                                <Badge variant="outline" className={
                                  jsa.compliance_analysis.compliance_status === 'COMPLIANT' ? 'text-green-600 border-green-600' :
                                  jsa.compliance_analysis.compliance_status === 'PARTIAL' ? 'text-orange-600 border-orange-600' :
                                  'text-red-600 border-red-600'
                                }>
                                  {jsa.compliance_analysis.compliance_status}
                                </Badge>
                              </div>

                              {/* Compliance Score */}
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="text-gray-600">Compliance Score:</span>
                                  <span className="font-semibold">{jsa.compliance_analysis.compliance_score}/100</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      jsa.compliance_analysis.compliance_score >= 80 ? 'bg-green-500' :
                                      jsa.compliance_analysis.compliance_score >= 60 ? 'bg-orange-500' :
                                      'bg-red-500'
                                    }`}
                                    style={{ width: `${jsa.compliance_analysis.compliance_score}%` }}
                                  />
                                </div>
                              </div>

                              {/* Summary */}
                              <p className="text-sm text-gray-700 mb-3">
                                {jsa.compliance_analysis.summary}
                              </p>

                              {/* Violations */}
                              {jsa.compliance_analysis.violations && jsa.compliance_analysis.violations.length > 0 && (
                                <div className="border-t pt-3">
                                  <p className="text-sm font-semibold text-red-700 mb-2">⚠️ Compliance Issues:</p>
                                  <div className="space-y-2">
                                    {jsa.compliance_analysis.violations.map((violation, vIdx) => (
                                      <div key={vIdx} className="bg-red-50 border-l-4 border-red-500 p-3 text-sm">
                                        <p className="font-medium text-red-900">JSA Requirement:</p>
                                        <p className="text-red-800 mb-2">{violation.jsa_requirement}</p>
                                        <p className="font-medium text-red-900">Audit Finding:</p>
                                        <p className="text-red-800 mb-1">{violation.audit_finding}</p>
                                        <Badge variant="outline" className={
                                          violation.severity === 'high' ? 'text-red-600 border-red-600' :
                                          violation.severity === 'medium' ? 'text-orange-600 border-orange-600' :
                                          'text-yellow-600 border-yellow-600'
                                        }>
                                          {violation.severity.toUpperCase()} Severity
                                        </Badge>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={handleClose}>{t('common.close')}</Button>
                  <Button onClick={handleSaveAiResults} className="bg-blue-600 hover:bg-blue-700">
                    Save Changes & Close
                  </Button>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <p>No AI results available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SafetyAuditDialog;